/**
 * My Plan — clean rewrite using the new billing design language.
 *
 * Shows: current plan summary, this-period usage, and the same PricingCards
 * row used in /pricing and the signup wizard so the whole product feels
 * consistent.
 */

import React, { useState } from 'react';
import { Box, Container, Typography, Card, Stack, Chip, LinearProgress, Button, Divider, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PricingCards, { BillingCycle, PlanName } from '../../billing/components/PricingCards';
import { useCurrentPlan } from '../../billing/hooks/useCurrentPlan';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface ApiPlan {
  id: string;
  name: string;
  displayName: string;
  priceMonthly: string | number;
  priceYearly: string | number;
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;
}

const INK = '#0B1220';
const ACCENT = '#3B5BFF';
const TEXT_MUTED = '#5B6478';
const BORDER = '#E5E8EF';

const formatMoney = (value: number, currency: string = 'USD'): string => {
  const rounded = Math.round(value);
  const prefix = currency === 'USD' ? 'US$' : `${currency} `;
  return `${prefix}${rounded.toLocaleString('en-US')}`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface MetricRowProps {
  label: string;
  used: number;
  limit: number | null;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, used, limit }) => {
  const hasLimit = typeof limit === 'number' && limit > 0;
  const pct = hasLimit ? Math.min(100, (used / (limit as number)) * 100) : 0;
  const isFull = pct >= 100;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.75}>
        <Typography sx={{ fontSize: 13, color: TEXT_MUTED, fontWeight: 500 }}>{label}</Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: isFull ? '#B42318' : INK,
            fontFeatureSettings: '"tnum" 1',
          }}
        >
          {used.toLocaleString()}
          <Box component="span" sx={{ color: TEXT_MUTED, fontWeight: 500, ml: 0.5 }}>
            / {hasLimit ? (limit as number).toLocaleString() : 'Unlimited'}
          </Box>
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={hasLimit ? pct : 0}
        sx={{
          height: 6,
          borderRadius: 4,
          backgroundColor: '#EEF1F5',
          '& .MuiLinearProgress-bar': {
            backgroundColor: isFull ? '#B42318' : INK,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: plan, isLoading } = useCurrentPlan();
  const [cycle, setCycle] = useState<BillingCycle>('YEARLY');
  const [selected, setSelected] = useState<PlanName | undefined>();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Load all plans so we can map plan NAME -> plan DB id for the checkout endpoint.
  const plansQ = useQuery({
    queryKey: ['subscription-plans-list'],
    queryFn: async (): Promise<ApiPlan[]> => {
      const r = await apiClient.get<{ success: boolean; data: ApiPlan[] }>('/subscriptions/plans');
      return r.data.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const checkoutMut = useMutation({
    mutationFn: async (args: { planName: PlanName; cycle: BillingCycle }) => {
      const target = (plansQ.data ?? []).find((p) => p.name === args.planName);
      if (!target) throw new Error(`Plan "${args.planName}" not available`);
      const r = await apiClient.post<{ success: boolean; data: { url: string } }>(
        '/subscriptions/create-checkout',
        { planId: target.id, billingCycle: args.cycle },
      );
      if (!r.data?.data?.url) throw new Error('Stripe did not return a checkout URL');
      return r.data.data.url;
    },
    onSuccess: (url) => {
      window.location.assign(url);
    },
    onError: (err: any) => {
      setCheckoutError(err?.response?.data?.message || err?.message || 'Checkout failed');
    },
  });

  // Surface ?payment=success | cancelled from Stripe redirect
  const paymentParam = searchParams.get('payment');
  React.useEffect(() => {
    if (paymentParam === 'success' || paymentParam === 'cancelled') {
      const t = window.setTimeout(() => {
        searchParams.delete('payment');
        setSearchParams(searchParams, { replace: true });
      }, 8000);
      return () => window.clearTimeout(t);
    }
  }, [paymentParam, searchParams, setSearchParams]);

  if (isLoading || !plan) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <LinearProgress />
      </Container>
    );
  }

  // Promo-redeemed accounts ride a "Custom" tier — they didn't pay for any
  // of the published plans, so none of the cards below should be highlighted
  // as "Current". They upgrade by checking out fresh from the catalog.
  const isTrial = plan.isTrial;
  const trialDaysLeft = plan.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(plan.trialEndsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  const planNameKey = isTrial ? undefined : (plan.plan as PlanName);

  const statusLabel = plan.isTrialExpired
    ? 'Trial ended'
    : isTrial
    ? `Trial${trialDaysLeft !== null ? ` — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : ''}`
    : plan.status === 'ACTIVE'
    ? 'Active'
    : plan.status === 'PENDING_PAYMENT'
    ? 'Awaiting payment'
    : plan.status;
  const statusOK = plan.status === 'ACTIVE' && !plan.isTrialExpired;
  const statusColor = plan.isTrialExpired ? '#B42318' : statusOK ? '#1B5E3A' : '#B54708';
  const statusBg = plan.isTrialExpired
    ? 'rgba(180, 35, 24, 0.10)'
    : statusOK
    ? 'rgba(34, 139, 87, 0.12)'
    : 'rgba(181, 71, 8, 0.10)';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Page header */}
      <Box mb={4}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: INK,
            mb: 0.5,
          }}
        >
          My Plan
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: 14 }}>
          Your current subscription, this period&rsquo;s usage, and upgrade options.
        </Typography>
      </Box>

      {paymentParam === 'success' && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Payment received. Your plan will update within a few seconds — refresh if it still looks old.
        </Alert>
      )}
      {paymentParam === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Checkout was cancelled. Your current plan is unchanged.
        </Alert>
      )}
      {checkoutError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setCheckoutError(null)}>
          {checkoutError}
        </Alert>
      )}
      {plan.isTrialExpired && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Your trial has ended.</strong> Pick a plan below to keep using lead generation, CRM, and assistant features.
        </Alert>
      )}
      {isTrial && !plan.isTrialExpired && trialDaysLeft !== null && trialDaysLeft <= 7 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Your trial ends in {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'}.</strong> Choose a plan now to avoid losing access.
        </Alert>
      )}

      {/* Current plan + usage row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 6,
        }}
      >
        {/* Current plan card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: TEXT_MUTED,
              mb: 2,
            }}
          >
            Current plan
          </Typography>

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontSize: 32,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: INK,
                  mb: 1,
                  lineHeight: 1.1,
                }}
              >
                {isTrial ? 'Custom' : (plan.planName || plan.plan)}
              </Typography>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  backgroundColor: statusBg,
                  color: statusColor,
                  border: `1px solid ${statusColor}33`,
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.04em',
                  height: 22,
                }}
              />
            </Box>
            <Box textAlign="right">
              <Typography
                sx={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontSize: 28,
                  fontWeight: 600,
                  color: INK,
                  letterSpacing: '-0.02em',
                  fontFeatureSettings: '"tnum" 1',
                  lineHeight: 1,
                }}
              >
                {isTrial
                  ? 'Free'
                  : plan.amount !== undefined
                  ? formatMoney(plan.amount, plan.currency || 'USD')
                  : '—'}
              </Typography>
              <Typography sx={{ color: TEXT_MUTED, fontSize: 12, mt: 0.5 }}>
                {isTrial ? 'while in trial' : `per ${plan.billingCycle === 'YEARLY' ? 'year' : 'month'}`}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ borderColor: BORDER, my: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2.5,
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 11, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                {isTrial ? 'Source' : 'Billing cycle'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: INK, fontWeight: 600 }}>
                {isTrial ? 'Promotion code' : plan.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                {isTrial ? 'Trial ends' : 'Renews on'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: INK, fontWeight: 600 }}>
                {formatDate(isTrial && plan.trialEndsAt ? plan.trialEndsAt : plan.currentPeriodEnd)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                User seats
              </Typography>
              <Typography sx={{ fontSize: 14, color: INK, fontWeight: 600 }}>
                {plan.maxUsers ?? 'Unlimited'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                Leads / month
              </Typography>
              <Typography sx={{ fontSize: 14, color: INK, fontWeight: 600 }}>
                {plan.maxLeads ?? 'Unlimited'}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/settings')}
              sx={{ color: TEXT_MUTED, textTransform: 'none', fontSize: 13 }}
            >
              Manage settings
            </Button>
          </Stack>
        </Card>

        {/* Usage card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: TEXT_MUTED,
              mb: 3,
            }}
          >
            This period
          </Typography>

          <Stack spacing={3}>
            <MetricRow label="Lead reveals" used={plan.currentLeads} limit={plan.maxLeads} />
            <MetricRow label="Team members" used={plan.currentUsers} limit={plan.maxUsers} />
          </Stack>

          <Divider sx={{ borderColor: BORDER, my: 3 }} />

          <Typography sx={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6 }}>
            Lead credits reset on {formatDate(plan.currentPeriodEnd)}. A reveal is
            counted only when you unlock a contact &mdash; browsing previews is
            always free.
          </Typography>
        </Card>
      </Box>

      {/* Available plans */}
      <Box mb={3}>
        <Typography
          sx={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: INK,
            mb: 0.5,
          }}
        >
          Available plans
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: 14 }}>
          Switch anytime. Yearly billing saves 25%.
        </Typography>
      </Box>

      <PricingCards
        cycle={cycle}
        onCycleChange={setCycle}
        // selectedPlan is the user's purchase INTENT (what they just clicked).
        // currentPlan is what they're already paying for. Keeping these two
        // separate is what fixes the "Selected and locked" trap when the
        // user has promo overrides that don't match any tier 1:1 — they can
        // still upgrade to a different plan without the page pretending they
        // already have what they want.
        selectedPlan={selected}
        currentPlan={planNameKey}
        currentPlanBadge={
          plan.status === 'PENDING_PAYMENT'
            ? 'Awaiting payment'
            : (plan as any).trialEndsAt
            ? 'Trial'
            : 'Current'
        }
        onSelectPlan={(p) => {
          // Allow upgrading away from the same tier IF the cycle changes
          // (e.g. monthly → yearly). Block exact same plan + cycle.
          if (p === planNameKey && cycle === plan.billingCycle) return;
          setSelected(p);
          setCheckoutError(null);
          checkoutMut.mutate({ planName: p, cycle });
        }}
        ctaLabel={checkoutMut.isPending ? 'Redirecting…' : 'Switch to this'}
      />
    </Container>
  );
};

export default SubscriptionPage;
